export const LB_PER_KG = 2.20462;
export const MACRO_GOALS = {
    loss: { label: 'Fat loss, 20% under', factor: 0.8 },
    maintain: { label: 'Maintenance', factor: 1 },
    gain: { label: 'Muscle gain, 10% over', factor: 1.1 },
};
/** The standard activity multipliers, BMR to total daily energy. */
export const ACTIVITY_LEVELS = [
    { factor: 1.2, label: 'Sedentary, little or no training' },
    { factor: 1.375, label: 'Lightly active, 1 to 3 days a week' },
    { factor: 1.55, label: 'Moderately active, 3 to 5 days a week' },
    { factor: 1.725, label: 'Very active, 6 to 7 days a week' },
    { factor: 1.9, label: 'Extra active, hard training or a physical job' },
];
export const PROTEIN_PER_LB = { min: 0.5, max: 1.5, default: 1.0 };
export const FAT_PCT = { min: 15, max: 40, default: 25 };
/** The member's stated goal, read as a calorie direction. Anything else is maintenance. */
export function macroGoalFor(goal) {
    const g = (goal ?? '').toLowerCase();
    if (g.includes('fat loss') || g.includes('lose'))
        return 'loss';
    if (g.includes('muscle') || g.includes('gain'))
        return 'gain';
    return 'maintain';
}
/* THE ACTIVITY LEVEL IS OBSERVED, not asked. Check-ins over the last 30 days, per
   week, against the bands the labels name. It never proposes Extra active: a
   physical job is not something attendance can see, so only a coach picks it. */
export function activityFor(visits30d) {
    const perWeek = ((visits30d ?? 0) * 7) / 30;
    if (perWeek < 1)
        return 1.2;
    if (perWeek < 3)
        return 1.375;
    if (perWeek < 6)
        return 1.55;
    return 1.725;
}
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const r = Math.round;
/**
 * Null, with the reason, when there is not enough to compute from. The reason is a
 * sentence for the screen: which input is missing, not that "something" is.
 */
export function computeMacros(i) {
    if (!(i.weightKg > 0))
        return { missing: 'a bodyweight' };
    const bf = i.bodyFatPct != null && i.bodyFatPct > 0 && i.bodyFatPct < 70 ? i.bodyFatPct : null;
    let bmr;
    let bmrMethod;
    let leanKg = null;
    const working = [];
    if (bf != null) {
        leanKg = i.weightKg * (1 - bf / 100);
        bmr = 370 + 21.6 * leanKg;
        bmrMethod = 'Katch-McArdle';
        working.push(['Lean mass', `${leanKg.toFixed(1)} kg = ${i.weightKg} kg × (1 − ${bf}%)`]);
        working.push(['BMR', `${r(bmr)} kcal = 370 + 21.6 × ${leanKg.toFixed(1)} (Katch-McArdle)`]);
    }
    else {
        const { heightCm, age, sex } = i;
        if (!(heightCm && heightCm > 0) || !(age && age > 0) || !sex) {
            return { missing: 'a body fat reading, or a height, age and sex' };
        }
        bmr = 10 * i.weightKg + 6.25 * heightCm - 5 * age + (sex === 'female' ? -161 : 5);
        bmrMethod = 'Mifflin-St Jeor';
        working.push(['BMR', `${r(bmr)} kcal (Mifflin-St Jeor, ${sex}, ${heightCm} cm, ${age} years)`]);
    }
    const goal = MACRO_GOALS[i.goal];
    const tdee = bmr * i.activity;
    const calories = tdee * goal.factor;
    const proteinPerLb = clamp(i.proteinPerLb, PROTEIN_PER_LB.min, PROTEIN_PER_LB.max);
    const fatPct = clamp(i.fatPct, FAT_PCT.min, FAT_PCT.max);
    const weightLb = i.weightKg * LB_PER_KG;
    const proteinG = proteinPerLb * weightLb;
    const fatG = (calories * fatPct) / 100 / 9;
    const carbRaw = calories - proteinG * 4 - fatG * 9;
    const carbG = Math.max(0, carbRaw) / 4;
    working.push(['Daily energy', `${r(tdee)} kcal = BMR × ${i.activity}`]);
    working.push(['Calories', `${r(calories)} kcal = ${r(tdee)} × ${goal.factor} (${goal.label.toLowerCase()})`]);
    working.push(['Protein', `${r(proteinG)} g = ${proteinPerLb} g × ${r(weightLb)} lb`]);
    working.push(['Fat', `${r(fatG)} g = ${fatPct}% of calories ÷ 9`]);
    working.push(['Carbohydrate', `${r(carbG)} g = what is left ÷ 4`]);
    const share = (kcal) => (calories > 0 ? r((kcal / calories) * 100) : 0);
    return {
        bmr, bmrMethod, leanKg, tdee, calories,
        proteinG, carbG, fatG,
        share: { protein: share(proteinG * 4), carb: share(carbG * 4), fat: share(fatG * 9) },
        working,
        carbsSqueezed: carbRaw < 0,
    };
}
/** The saved shape, rounded the way the screen shows it. */
export function toTargets(i, res, o) {
    return {
        ...i,
        calories: r(res.calories), proteinG: r(res.proteinG), carbG: r(res.carbG), fatG: r(res.fatG),
        bmr: r(res.bmr), bmrMethod: res.bmrMethod, tdee: r(res.tdee),
        ...o,
    };
}
/** One line for a timeline entry. */
export function macroSummary(t) {
    return `${t.calories} kcal a day: protein ${t.proteinG} g, carbohydrate ${t.carbG} g, fat ${t.fatG} g`;
}
